import React, { useState } from "react";
import {
  Box,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import { ReactComponent as LicenseTypeIcon } from "../../../../assets/dashboard/globalFilters/LicenseTypeIcon.svg";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { licenseTypeOptions } from "../../../../components/customerManagement/steps";
import CustomDateRangePicker from "./custom-date-range";

import {
  IconWrapper,
  StyledGlobalFiltersContainer,
  StyledGlobalFiltersFields,
  StyledPrimaryButton,
  StyledResetButton,
  StyledPeriodLabel,
  RegularText,
} from "../styles";
import colors from "../../../../theme/Colours";

interface GlobalFiltersProps {
  onSave: (formData: {
    fromDate: string;
    toDate: string;
    licenseType: string;
  }) => void;
  onReset: () => void;
  disabled?: boolean;
  resetDisabled?: boolean;
  trialDaysRemaining?: number;
}

const inputFieldStyles = {
  backgroundColor: "#fff",
  "& .MuiOutlinedInput-root": {
    height: "42px",
    display: "flex",
    alignItems: "center",
  },
  "& .MuiInputBase-input": {
    padding: "10px 12px",
    display: "flex",
    alignItems: "center",
  },
  "& fieldset": {
    borderColor: "#ccd5dd",
  },
  "&:hover fieldset": {
    borderColor: "#ccd5dd",
  },
  "&.Mui-focused fieldset": {
    borderColor: "#ccd5dd",
  },
};

const GlobalFilters: React.FC<GlobalFiltersProps> = ({
  onSave,
  onReset,
  disabled = false,
  resetDisabled = false,
  trialDaysRemaining,
}) => {
  console.log("GlobalFilters trialDaysRemaining:", trialDaysRemaining);
  const [form, setForm] = useState({
    fromDate: "",
    toDate: "",
    licenseType: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleApply = () => {
    onSave(form);
  };

  const handleReset = () => {
    setForm({
      fromDate: "",
      toDate: "",
      licenseType: "",
    });
    onReset();
  };

  const setGlobalFromDate = (date: Date | undefined) => {
    setForm((prev) => ({
      ...prev,
      fromDate: date ? moment(date).format("YYYY-MM-DD") : "",
    }));
  };

  const setGlobalToDate = (date: Date | undefined) => {
    setForm((prev) => ({
      ...prev,
      toDate: date ? moment(date).format("YYYY-MM-DD") : "",
    }));
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <StyledGlobalFiltersContainer
        sx={{
          opacity: disabled ? 0.6 : 1,
          pointerEvents: disabled ? "none" : "auto",
          mt: 0,
          mb: 1,
        }}
      >
        <StyledGlobalFiltersFields>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              height: 40,
              flexShrink: 0,
              mr: 1,
            }}
          >
            <FilterListIcon sx={{ fontSize: 16, color: "#667085" }} />
            <StyledPeriodLabel sx={{ fontSize: "14px" }}>
              Filters
            </StyledPeriodLabel>
          </Box>

          <div
            style={{
              width: "1px",
              height: "44px",
              backgroundColor: "#E0E0E0",
            }}
          />

          <Box sx={{ width: 220, flexShrink: 0, paddingRight: 3 }}>
            <StyledPeriodLabel>Date Range</StyledPeriodLabel>
            <CustomDateRangePicker
              fromDate={
                form.fromDate ? moment(form.fromDate).toDate() : undefined
              }
              toDate={form.toDate ? moment(form.toDate).toDate() : undefined}
              setFromDate={setGlobalFromDate}
              setToDate={setGlobalToDate}
              disableFuture
              showReset
              sx={{
                height: "40px",
              }}
            />
          </Box>

          <Box sx={{ width: 220, flexShrink: 0 }}>
            <StyledPeriodLabel>License Type</StyledPeriodLabel>
            <TextField
              name="licenseType"
              select
              value={form.licenseType}
              onChange={handleChange}
              fullWidth
              size="small"
              sx={{ ...inputFieldStyles }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IconWrapper as={LicenseTypeIcon} />
                  </InputAdornment>
                ),
              }}
            >
              {licenseTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </StyledGlobalFiltersFields>

        <Box
          display="flex"
          alignItems="center"
          gap={1.5}
          sx={{ flexShrink: 0 }}
        >
          <StyledResetButton
            sx={{ height: "36px" }}
            variant="outlined"
            size="small"
            onClick={handleReset}
            disabled={resetDisabled}
          >
            Reset
          </StyledResetButton>

          <StyledPrimaryButton
            variant="contained"
            size="small"
            onClick={handleApply}
            sx={{ height: "36px" }}
          >
            Apply
          </StyledPrimaryButton>
          <div
            style={{
              width: "1px",
              height: "32px",
              backgroundColor: "#E0E0E0",
            }}
          />
          {trialDaysRemaining !== undefined && (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                px: "12px",
                py: "4px",
                borderRadius: "999px", // cylindrical / pill shape
                border: "1px solid",
                borderColor: colors.primaryButtonOrange,
                backgroundColor: "#faf0e8", // very very light orange
              }}
            >
              <RegularText
                style={{
                  color: colors.primaryButtonOrange,
                  fontSize: "12px",
                  fontWeight: 500,
                }}
              >
                {trialDaysRemaining <= 0
                  ? "Trial ended"
                  : `Free Trial - ${trialDaysRemaining} days remaining`}
              </RegularText>
            </Box>
          )}
        </Box>
      </StyledGlobalFiltersContainer>
    </LocalizationProvider>
  );
};

export default GlobalFilters;
